import { PercentPipe } from '@angular/common';
import { Component, computed, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { AssistantKey } from '@vendasta/protos/ai_assistants/v1alpha1';
import { of } from 'rxjs';
import { catchError, distinctUntilChanged, map, startWith, switchMap } from 'rxjs/operators';
import { AiAssistantI18nModule } from '../../assets/i18n/ai-assistant-i18n.module';
import { JobStat, JobsToBeDoneService } from './jobs-to-be-done.service';
import { JobDetailModalComponent, JobDetailModalData } from './job-detail-modal/job-detail-modal.component';

type SuccessRateTone = 'high' | 'medium' | 'low';

// Maps a pass rate (0-1) to a tone bucket that drives the rate's color.
export function successRateTone(rate: number): SuccessRateTone {
  const percent = Math.round(rate * 100);
  if (percent >= 80) return 'high';
  if (percent >= 60) return 'medium';
  return 'low';
}

type JobsState = {
  status: 'loading' | 'loaded' | 'error';
  jobs: JobStat[];
};

@Component({
  selector: 'ai-jobs-to-be-done',
  imports: [PercentPipe, MatCardModule, GalaxyLoadingSpinnerModule, GalaxyAlertModule, AiAssistantI18nModule],
  templateUrl: './jobs-to-be-done.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './jobs-to-be-done.component.scss',
})
export class JobsToBeDoneComponent {
  private readonly jobsToBeDoneService = inject(JobsToBeDoneService);
  private readonly dialog = inject(MatDialog);

  readonly assistantKey = input.required<AssistantKey>();

  protected readonly tone = successRateTone;

  private readonly state = toSignal(
    toObservable(this.assistantKey).pipe(
      // The key input can arrive as a new object with the same value (e.g. a
      // parent computed that re-emits on unrelated changes); only refetch when
      // the assistant actually changes.
      distinctUntilChanged((a, b) => a.id === b.id && JSON.stringify(a.namespace) === JSON.stringify(b.namespace)),
      switchMap((key) =>
        this.jobsToBeDoneService.listJobsToBeDone(key).pipe(
          map((jobs): JobsState => ({ status: 'loaded', jobs })),
          startWith<JobsState>({ status: 'loading', jobs: [] }),
          catchError(() => of<JobsState>({ status: 'error', jobs: [] })),
        ),
      ),
    ),
    { initialValue: { status: 'loading', jobs: [] } as JobsState },
  );

  readonly jobs = computed(() => this.state().jobs);
  readonly loading = computed(() => this.state().status === 'loading');
  readonly errored = computed(() => this.state().status === 'error');

  openDetail(stat: JobStat): void {
    this.dialog.open<JobDetailModalComponent, JobDetailModalData>(JobDetailModalComponent, {
      data: { stat, assistantKey: this.assistantKey() },
      maxHeight: '90vh',
      autoFocus: false,
    });
  }
}
